import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCurrency } from '@travel/i18n';
import { ArrowDown, ArrowUp } from '@travel/icons/ui';
import { useCurrencyChangeCallback, useGetTranslation } from '@travel/traveler-core/hooks';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { CouponDetail as UICouponDetail, FlatButton } from '@travel/ui';
import { cx } from '@travel/utils';

import { fetchCouponDetails } from 'store/salesPromotion/actions';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import { Translate } from 'core/translate';
import { useCouponDetailLabels } from 'hooks/useCouponLabels';
import type { CouponDetails } from 'SalesPromotion-Types';
import { shouldDisplayCouponDisclaimer } from 'utils/salesPromotion';

import styles from './couponAccordion.module.scss';

type Props = {
  /** String of title accordion className */
  className?: string;
  /** String of Coupon content className */
  couponContentClassName?: string;
  /** String of target coupon id */
  couponId: string;
  /** Function to remove Coupon from the search */
  onRemoveCoupon?: () => void;
  /** Should render small list */
  isSmallList?: boolean;
  /** is Open/Closed should be used together width toggle prop func */
  isOpen?: boolean;
  /** toggle Open/Close prop func */
  onToggle?: () => void;
};

function CouponAccordion(props: Props) {
  const { className, couponContentClassName, onRemoveCoupon, isSmallList, isOpen, onToggle } =
    props;

  const [showContent, setShowContent] = useState(typeof isOpen === 'boolean' ? isOpen : true);

  const dispatch = useDispatch();
  const currency = useSelector(getCurrency);

  const getTranslation = useGetTranslation();

  const coupon: CouponDetails = useSelector(getCouponDetails(props.couponId));
  const labels = useCouponDetailLabels(getTranslation, currency, coupon?.localCurrency);

  const isMobile = useDeviceType() === MOBILE_SCREEN;

  const onCurrencyChange = useCallback(
    () => dispatch(fetchCouponDetails(props.couponId, true)),
    [dispatch, props.couponId],
  );

  useCurrencyChangeCallback(onCurrencyChange);

  useEffect(() => {
    if (typeof isOpen === 'boolean' && onToggle && isOpen !== showContent) {
      setShowContent(isOpen);
    }
  }, [isOpen, onToggle]);

  if (!coupon) {
    return null;
  }

  const onToggleView = () => {
    if (onToggle) {
      onToggle();
    } else {
      setShowContent((prev) => !prev);
    }
  };

  const collapseIcon = (
    <ArrowUp color={isMobile ? 'cilantro' : 'sonicSilver'} size={isMobile ? 12 : 24} />
  );
  const expandIcon = (
    <ArrowDown color={isMobile ? 'cilantro' : 'sonicSilver'} size={isMobile ? 12 : 24} />
  );

  return (
    <div
      className={cx(
        styles.wrapper,
        className,
        showContent && styles.open,
        isSmallList && styles.forceSP,
      )}
      data-testid="couponAccordion-wrapper"
    >
      <div className={styles.topButtons}>
        <FlatButton
          data-testid="couponAccordion-toggleButton"
          classType="secondary"
          className={styles.toggleButton}
          onClick={onToggleView}
        >
          <Translate className={styles.text} id="Coupon_Details.Conditions.Title" />
          <span className={styles.icon}>{showContent ? collapseIcon : expandIcon}</span>
        </FlatButton>

        {isMobile && (
          <FlatButton
            data-testid="couponAccordion-removeButton"
            classType="secondary"
            className={styles.removeButton}
            onClick={onRemoveCoupon}
          >
            <Translate id="Coupon_Details.Remove" />
          </FlatButton>
        )}
      </div>
      {showContent && (
        <>
          {shouldDisplayCouponDisclaimer(coupon, currency) && (
            <Translate
              className={styles.annotation}
              id="Coupon_Details.Price_Disclaimer"
              condition={() => `status == different_currency`}
            />
          )}
          {isMobile && (
            <h4 className={styles.header}>
              <Translate id="Coupon_Details.Conditions.Title" />
            </h4>
          )}
          <UICouponDetail
            className={cx(styles.content, couponContentClassName)}
            couponTextClassName={styles.couponText}
            customHeaderTitle={''}
            couponDetail={coupon}
            currency={currency}
            labels={labels}
          />
        </>
      )}
    </div>
  );
}

export default CouponAccordion;
